/**
 * Creates a debounced version of the provided function.
 *
 * @template T - The type of the arguments passed to the function.
 * @param {(...args: T) => void} func - The function to debounce.
 * @param {number} [timeout=500] - The number of milliseconds to delay.
 * @returns {(...args: T) => void} A new debounced function.
 *
 * @description
 * The returned function will delay invoking `func` until after `timeout` milliseconds have elapsed
 * since the last time the debounced function was invoked.
 */
export default function debouncedFunc<T extends unknown[]>(
  func: (...args: T) => void,
  timeout: number = 500,
): (...args: T) => void {
  let debounceTimer: ReturnType<typeof setTimeout>;
  return function (...args: T): void {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      func(...args);
    }, timeout);
  };
}

/**
 * Creates a throttled and debounced version of the provided function.
 *
 * @template T - The type of the arguments passed to the function.
 * @param {(...args: T) => void} func - The function to throttle and debounce.
 * @param {number} interval - The throttle interval.
 * @param {number} timeout - The debounce timeout in milliseconds.
 * @returns {(...args: T) => void} A new throttled and debounced function.
 *
 * @description
 * This function combines throttling and debouncing:
 * - It will call the function immediately when the throttle interval is reached.
 * - If the throttle interval isn't reached, it will debounce the call.
 */
export function throttledDebounce<T extends unknown[]>(
  func: (...args: T) => void,
  interval: number = 10,
  timeout: number = 500,
): (...args: T) => void {
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let throttleCounter: number = 0;
  return function (...args: T): void {
    throttleCounter++;
    const callFunc = () => {
      func(...args);
      throttleCounter = 0;
    };
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    if (throttleCounter % interval === 0) {
      callFunc();
    } else {
      debounceTimer = setTimeout(callFunc, timeout);
    }
  };
}
